import { bffFetch } from "@/shared/api/client";

export type PartyKind = "individual" | "corporation";

export interface PartyWire {
  id?: string;
  name?: string | null;
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  contact_emails?: ContactEmailRow[] | null;
  contact_phones?: ContactPhoneRow[] | null;
  identification_numbers?: IdentificationNumberRow[] | null;
  addresses?: AddressRow[] | null;
}

export interface ContactEmailRow {
  id?: string;
  email_address?: string;
  primary?: boolean;
}

interface ContactPhoneRow {
  id?: string;
  phone_number?: string;
  primary?: boolean;
}

export interface IdentificationNumberRow {
  id?: string;
  value?: string;
  issuing_country_id?: string;
  type?: string;
  valid_from_inclusive?: string | null;
  valid_to_inclusive?: string | null;
}

export interface AddressRow {
  id?: string;
  address?: {
    address_line_1?: string | null;
    address_line_2?: string | null;
    zip_code?: string | null;
    city?: string | null;
    administrative_area?: string | null;
    country_id?: string | null;
  } | null;
  address_type?: string | null;
}

export async function readParty(kind: PartyKind, partyId: string): Promise<PartyWire | null> {
  return readOptional<PartyWire>(`v1/${kind}/${seg(partyId)}`);
}

export async function readPartyIdOfClient(
  kind: PartyKind,
  clientLinkId: string,
): Promise<string | null> {
  const link = await readOptional<Partial<Record<`${PartyKind}_id`, string>>>(
    `v1/${kind}-client/${seg(clientLinkId)}`,
  );
  return (link?.[`${kind}_id`] ?? "").trim() || null;
}

export function partyNameOf(party: PartyWire | null): string {
  const name = (party?.name ?? "").trim();
  if (name) return name;
  return [party?.first_name, party?.last_name]
    .map((part) => (part ?? "").trim())
    .filter((part) => part !== "")
    .join(" ");
}

export function contactEmailsOf(party: PartyWire | null): ContactEmailRow[] {
  return Array.isArray(party?.contact_emails) ? party.contact_emails : [];
}

export async function writePartyContactEmail(
  kind: PartyKind,
  partyId: string,
  contactEmailId: string | null,
  address: string,
): Promise<void> {
  const base = `v1/${kind}/${seg(partyId)}/contact-email`;
  if (contactEmailId) {
    await writeParty("PATCH", `${base}/${seg(contactEmailId)}`, { email_address: address });
  } else {
    await writeParty("POST", base, { email_address: address, primary: true });
  }
}

export async function writeParty(
  method: "POST" | "PATCH",
  path: string,
  body: unknown,
): Promise<void> {
  const response = await bffFetch(path, { method, json: body });
  if (!response.ok) {
    throw new PartyWriteError(method, path, response.status, await response.text().catch(() => ""));
  }
}

export class PartyWriteError extends Error {
  constructor(
    readonly method: "GET" | "POST" | "PATCH",
    readonly path: string,
    readonly status: number,
    readonly bffMessage: string = "",
  ) {
    super(`BFF ${method} ${path} answered ${status}`);
    this.name = "PartyWriteError";
  }
}

async function readOptional<T>(path: string): Promise<T | null> {
  const response = await bffFetch(path);
  if (!response.ok) return null;
  return (await response.json()) as T;
}

const seg = encodeURIComponent;
